import { useState } from "react";
import { jsPDF } from "jspdf";
import autoTable from "jspdf-autotable";
import { styles } from "../styles";
import BackButton from "../components/BackButton";
import {
  exportPdf,
  loadImageAsDataUrl,
  printPdf,
  shouldShowPdfChooser,
} from "../utils/pdfExport";
import PdfActionModal from "../components/PdfActionModal";
import { isEffectivelyTaxExempt } from "../utils/billTotals";

const num = (v) => {
  const n = parseFloat(v);
  return Number.isFinite(n) ? n : 0;
};
const money = (n) =>
  n.toLocaleString("en-US", { style: "currency", currency: "USD" });

/**
 * Phase 5.0d — read-only quote view. The contractor signs during prep (in the
 * editor); here they Send it (→ status "sent") and Print it. The printable
 * copy shows the contractor's electronic signature and a blank ink line for
 * the customer to sign (for customers who don't use the app). In-app customer
 * e-signing is a future feature.
 */
// Bottom margin for quote PDFs. autoTable + ensureSpace use this to
// stop content drawing before it crowds the centered footer. ~1.25"
// of breathing room.
const QUOTE_BOTTOM_MARGIN = 90;
// Top margin on pages 2+ -- leaves clearance for stampPageHeaders.
const QUOTE_TOP_MARGIN_PAGE2 = 70;

// Conditionally page-break before drawing a vertically-tall block
// (totals, signature block) so it doesn't split across pages and
// confuse readers if pages get separated. Returns the y the caller
// should use for its next draw. Pass needed = approx pixel height
// of the block about to render.
const ensureQuoteSpace = (doc, y, needed) => {
  const pageH = doc.internal.pageSize.getHeight();
  if (y + needed > pageH - QUOTE_BOTTOM_MARGIN) {
    doc.addPage();
    return QUOTE_TOP_MARGIN_PAGE2;
  }
  return y;
};

// Stamps a small identification header on EVERY page 2+ of a multi-page
// quote PDF. Loose-page problem: customers print a 3-page quote, the
// stack gets dropped, page 2 by itself has no Quote # or company name
// on it. This puts logo (compact) + org name + doc number on the left,
// "Page X of Y" on the right, thin separator line below. No-op for
// single-page docs.
const stampPageHeaders = (doc, opts = {}) => {
  const pageCount = doc.internal.getNumberOfPages();
  if (pageCount < 2) return;

  const { logoData, orgName, quoteNumber, isMaintenance, copyTag, revision } =
    opts;
  const pageW = doc.internal.pageSize.getWidth();
  const MARGIN = 40;
  const docType = isMaintenance ? "Maintenance Agreement" : "Quote";
  const revisionTag = revision > 0 ? ` · Rev ${revision}` : "";
  const subline = [
    `${docType} ${quoteNumber || ""}${revisionTag}`.trim(),
    copyTag,
  ]
    .filter(Boolean)
    .join(" — ");

  for (let p = 2; p <= pageCount; p++) {
    doc.setPage(p);
    let leftX = MARGIN;
    if (logoData) {
      try {
        doc.addImage(logoData, "AUTO", MARGIN, 18, 30, 22);
        leftX = MARGIN + 40;
      } catch {
        // bad image data; just skip the logo for headers, don't bail
      }
    }
    doc.setFont("helvetica", "bold");
    doc.setFontSize(11);
    doc.setTextColor(34, 34, 34);
    if (orgName) doc.text(orgName, leftX, 28);
    doc.setFont("helvetica", "normal");
    doc.setFontSize(10);
    doc.setTextColor(85, 85, 85);
    doc.text(subline, leftX, 42);
    const pageStr = `Page ${p} of ${pageCount}`;
    const pageStrW = doc.getTextWidth(pageStr);
    doc.text(pageStr, pageW - MARGIN - pageStrW, 28);
    doc.setDrawColor(200);
    doc.setLineWidth(0.5);
    doc.line(MARGIN, 52, pageW - MARGIN, 52);
    doc.setFont("helvetica", "normal");
    doc.setTextColor(0);
  }
};

// Stamps a centered footer line on EVERY page of the given jsPDF doc.
// Used by both the customer copy and the office copy when the org has a
// quoteDefaults.footer set (e.g. "TX License #TACL12345. Thanks!"). No-op
// when text is empty.
const stampCenteredFooter = (doc, text) => {
  if (!text || !text.trim()) return;
  const pageWidth = doc.internal.pageSize.getWidth();
  const pageHeight = doc.internal.pageSize.getHeight();
  const pageCount = doc.internal.getNumberOfPages();
  doc.setFontSize(8);
  doc.setTextColor(110);
  doc.setFont(undefined, "italic");
  const lines = doc.splitTextToSize(text.trim(), pageWidth - 80);
  const lineHeight = 11;
  const totalHeight = lines.length * lineHeight;
  for (let p = 1; p <= pageCount; p++) {
    doc.setPage(p);
    doc.text(lines, pageWidth / 2, pageHeight - 24 - totalHeight + lineHeight, {
      align: "center",
    });
  }
  doc.setFont(undefined, "normal");
  doc.setTextColor(0);
};

export default function QuoteView({
  quote,
  actions,
  customer,
  location,
  customerName,
  locationName,
  jobsRaw = [],
  canSeeMargin,
  canSend,
  canMarkApproved,
  logoUrl = "",
  orgName = "",
  orgAddress = {},
  quoteDefaults,
  onEdit,
  onBack,
}) {
  const [sending, setSending] = useState(false);
  const [approvingJob, setApprovingJob] = useState(false);
  // Open snapshot modal when admin clicks a row in the revision history.
  // null = closed; otherwise = the snapshot object from revisionHistory[].
  const [viewingRevision, setViewingRevision] = useState(null);
  const c = quote.costing || {};
  const isMaintenance = quote.quoteType === "maintenance";

  // Maintenance preview math — mirrors QuoteEditor + addContract so the
  // numbers shown here match what the customer signed for.
  const visitsPerYear = isMaintenance
    ? { monthly: 12, quarterly: 4, biannual: 2, annual: 1 }[
        quote.contractVisitInterval || "quarterly"
      ] || 4
    : 0;
  const contractTermY = num(quote.contractTermYears || 1) || 1;
  const totalVisits = visitsPerYear * contractTermY;
  const visitIntervalLabel = {
    monthly: "Monthly",
    quarterly: "Quarterly",
    biannual: "Biannual",
    annual: "Annual",
  }[quote.contractVisitInterval || "quarterly"];
  const billingModeLabel =
    quote.contractBillingMode === "per-interval"
      ? `Per interval (billed every ${quote.contractBillingInterval || "quarter"})`
      : "Per visit (billed when visit complete)";

  const lineWithMarkup = (l) =>
    num(l.qty) * num(l.cost) * (1 + num(l.markup) / 100);

  const laborPrice = (c.labor || []).reduce(
    (s, l) => s + num(l.hours) * num(l.price),
    0
  );
  const materialsPrice = (c.materials || []).reduce(
    (s, m) => s + lineWithMarkup(m),
    0
  );
  const chargesPrice = (c.charges || []).reduce(
    (s, x) => s + lineWithMarkup(x),
    0
  );
  // Phase fuel-on-quote: mileage or flat trip line. Same helpers as
  // QuoteEditor + JobCosting so totals stay consistent across the trio.
  const fuelLineCost = (f) =>
    f.mode === "flat"
      ? num(f.amount)
      : num(f.costPerMile) * num(f.milesOneWay) * 2 * num(f.trips);
  const fuelLineWithMarkup = (f) =>
    fuelLineCost(f) * (1 + num(f.markup) / 100);
  const fuelPrice = (c.fuel || []).reduce(
    (s, f) => s + fuelLineWithMarkup(f),
    0
  );
  const priceSubtotal =
    laborPrice + materialsPrice + chargesPrice + fuelPrice;

  // Tax respects per-line taxable flag + customer-level exempt status.
  // Mirrors the QuoteEditor logic; computed here so the read-only view +
  // PDF show the same totals as the editor would.
  const isTaxable = (line) => line?.taxable !== false;
  const taxableSubtotal =
    (c.labor || []).reduce(
      (s, l) => (isTaxable(l) ? s + num(l.hours) * num(l.price) : s),
      0
    ) +
    (c.materials || []).reduce(
      (s, m) => (isTaxable(m) ? s + lineWithMarkup(m) : s),
      0
    ) +
    (c.charges || []).reduce(
      (s, x) => (isTaxable(x) ? s + lineWithMarkup(x) : s),
      0
    ) +
    (c.fuel || []).reduce(
      (s, f) => (isTaxable(f) ? s + fuelLineWithMarkup(f) : s),
      0
    );
  // Effective exempt = taxExempt flag AND cert not expired. Expired cert
  // means tax applies until renewal (matches the bill/quote editors).
  const customerExempt = isEffectivelyTaxExempt(customer);
  const taxTotal = customerExempt
    ? 0
    : (c.taxes || []).reduce(
        (s, t) =>
          s +
          (t.mode === "flat"
            ? num(t.amount)
            : taxableSubtotal * (num(t.rate) / 100)),
        0
      );
  const grandTotal = priceSubtotal + taxTotal;

  const totalCost =
    (c.labor || []).reduce((s, l) => s + num(l.hours) * num(l.cost), 0) +
    (c.materials || []).reduce((s, m) => s + num(m.qty) * num(m.cost), 0) +
    (c.charges || []).reduce((s, x) => s + num(x.qty) * num(x.cost), 0) +
    (c.fuel || []).reduce((s, f) => s + fuelLineCost(f), 0);
  const margin = priceSubtotal - totalCost;
  const marginPct = priceSubtotal > 0 ? (margin / priceSubtotal) * 100 : 0;
  const markupPct = totalCost > 0 ? (margin / totalCost) * 100 : 0;

  const isDraft = quote.status === "draft";
  const sig = quote.contractorSignature;

  const expiry =
    quote.sentAt && quote.validDays
      ? new Date(
          new Date(quote.sentAt).getTime() +
            quote.validDays * 24 * 60 * 60 * 1000
        ).toLocaleDateString()
      : null;

  const send = async () => {
    if (!sig) {
      window.alert(
        "Add the contractor signature first — open Edit and sign the quote before sending."
      );
      return;
    }
    const ok = window.confirm(
      "Send this quote to the customer?\n\nIt becomes finalized and goes out for approval. (You won't edit it after sending.)"
    );
    if (!ok) return;
    setSending(true);
    await actions.updateQuote(quote.id, {
      status: "sent",
      sentAt: new Date().toISOString(),
      updatedAt: new Date().toISOString(),
    });
    setSending(false);
  };

  // Amend a sent quote -- snapshots prior state into revisionHistory,
  // bumps revision, flips back to draft so the user can re-edit. Same
  // quote number across all revisions.
  const [amending, setAmending] = useState(false);
  const handleAmend = async () => {
    const ok = window.confirm(
      `Amend this quote?\n\nThe current version will be saved as Rev ${
        quote.revision || 0
      } in the revision history. You can edit and re-send as Rev ${
        (quote.revision || 0) + 1
      }. The contractor signature will need to be re-captured.`
    );
    if (!ok) return;
    try {
      setAmending(true);
      await actions.amendQuote(quote.id);
      onEdit && onEdit();
    } catch (e) {
      window.alert(e.message);
    } finally {
      setAmending(false);
    }
  };

  const markApproved = async () => {
    const message = isMaintenance
      ? `Mark this maintenance quote APPROVED?\n\nThis locks the quote and creates a maintenance contract from it:\n  • ${totalVisits} visit${
          totalVisits === 1 ? "" : "s"
        } spawned (${visitsPerYear}/yr × ${contractTermY} yr)\n  • Starts ${
          quote.contractStartDate || "(start date not set)"
        }\n  • ${billingModeLabel}\n\nThe contract appears on the Contracts page; visits live on the Jobs board as "scheduled" until you activate them.`
      : "Mark this quote APPROVED?\n\nThis locks the quote and creates a job from it (on the Jobs board) with its Estimated Bill seeded from the quote.";
    const ok = window.confirm(message);
    if (!ok) return;
    setApprovingJob(true);
    if (isMaintenance) {
      await actions.approveQuoteAndCreateContract(quote.id);
    } else {
      await actions.approveQuoteAndCreateJob(quote.id);
    }
    setApprovingJob(false);
  };

  const [pdfPending, setPdfPending] = useState(null);
  const handlePdfPrint = () => {
    if (!pdfPending) return;
    printPdf(pdfPending.doc);
    setPdfPending(null);
  };
  const handlePdfSave = async () => {
    if (!pdfPending) return;
    await exportPdf(pdfPending.doc, pdfPending.filename);
    setPdfPending(null);
  };
  // Customer-facing export format. Mirrors the JobCosting bill toggle:
  //   summary   — scope + a single Total line (no line items)
  //   breakdown — labor, materials, charges, taxes line-by-line + total
  const [quoteFormat, setQuoteFormat] = useState("summary");

  // Customer-facing printable quote: prices only (no cost/margin), the
  // contractor's electronic signature, and a blank ink line for the customer.
  const printQuote = async (format = "summary") => {
    const linkedJob = quote.jobId
      ? jobsRaw.find((j) => j.id === quote.jobId)
      : null;
    const metaLabel = linkedJob
      ? `Job #${linkedJob.jobNumber || "—"}${
          linkedJob.title ? ` — ${linkedJob.title}` : ""
        }`
      : `Quote #${quote.quoteNumber || "—"}`;

    const addrLineList = (addr = {}) => {
      const lines = [];
      if (addr.street) lines.push(addr.street);
      const cityLine = [addr.city, addr.state, addr.zip]
        .filter(Boolean)
        .join(", ");
      if (cityLine) lines.push(cityLine);
      if (addr.phone) lines.push(addr.phone);
      return lines;
    };

    const doc = new jsPDF({ unit: "pt", format: "letter" });
    const PAGE_W = doc.internal.pageSize.getWidth();
    const MARGIN = 40;
    const CONTENT_W = PAGE_W - MARGIN * 2;

    // --- Header ---
    const logoData = await loadImageAsDataUrl(logoUrl);
    let titleX = MARGIN;
    if (logoData) {
      try {
        doc.addImage(logoData, "AUTO", MARGIN, MARGIN, 80, 60);
        titleX = MARGIN + 90;
      } catch (err) {
        console.warn("logo addImage failed:", err);
      }
    }
    const revisionTag =
      (quote.revision || 0) > 0 ? ` · Rev ${quote.revision}` : "";
    const heading = `${
      isMaintenance ? "Maintenance Agreement" : "Quote"
    } ${quote.quoteNumber || ""}${revisionTag}`.trim();
    doc.setFont("helvetica", "bold");
    doc.setFontSize(18);
    doc.setTextColor(34, 34, 34);
    doc.text(heading, titleX, MARGIN + 18);

    let headerY = MARGIN + 18;
    if (orgName) {
      doc.setFont("helvetica", "normal");
      doc.setFontSize(11);
      doc.setTextColor(102, 102, 102);
      headerY += 16;
      doc.text(orgName, titleX, headerY);
    }
    doc.setFontSize(10);
    doc.setTextColor(85, 85, 85);
    for (const line of addrLineList(orgAddress)) {
      headerY += 12;
      doc.text(line, titleX, headerY);
    }
    headerY = Math.max(headerY, MARGIN + 60);

    // Supersedes line -- only on amended quotes. Tells the customer
    // this version replaces a prior one, and when that prior version
    // was issued. Reads ABOVE the meta block so it's the first thing
    // they see after the header.
    if ((quote.revision || 0) > 0) {
      const lastRev = (quote.revisionHistory || []).slice(-1)[0];
      const prevSentAt = lastRev?.sentAt
        ? new Date(lastRev.sentAt).toLocaleDateString()
        : "earlier";
      const prevLabel =
        (quote.revision || 0) === 1
          ? "the original quote"
          : `Rev ${(quote.revision || 0) - 1}`;
      doc.setFont("helvetica", "italic");
      doc.setFontSize(10);
      doc.setTextColor(154, 107, 0);
      const supersedesLine = `This revision supersedes ${prevLabel} issued ${prevSentAt}.`;
      doc.text(supersedesLine, MARGIN, headerY + 18);
      headerY += 18;
      doc.setFont("helvetica", "normal");
      doc.setTextColor(85, 85, 85);
    }

    // --- Doc meta: Job# / Quote# + Date ---
    let y = headerY + 28;
    doc.setFont("helvetica", "bold");
    doc.setFontSize(14);
    doc.setTextColor(26, 95, 180);
    doc.text(metaLabel, MARGIN, y);
    doc.setFont("helvetica", "normal");
    doc.setFontSize(11);
    doc.setTextColor(34, 34, 34);
    const dateStr = `Date: ${new Date().toLocaleDateString()}`;
    doc.text(dateStr, PAGE_W - MARGIN - doc.getTextWidth(dateStr), y);

    // --- Parties ---
    y += 22;
    const partyStartY = y;
    const colW = (CONTENT_W - 20) / 2;
    const locColX = MARGIN + colW + 20;
    const showLoc = !!quote.locationId;

    doc.setFont("helvetica", "bold");
    doc.setFontSize(11);
    doc.text("Bill To", MARGIN, y);
    if (showLoc) doc.text("Service Location", locColX, y);
    y += 14;
    doc.setFont("helvetica", "normal");
    doc.text(customer?.name || customerName || "—", MARGIN, y);
    if (showLoc) doc.text(location?.name || locationName || "—", locColX, y);
    y += 12;

    const billLines = addrLineList({
      street: customer?.billingStreet,
      city: customer?.billingCity,
      state: customer?.billingState,
      zip: customer?.billingZip,
    });
    const locLines = showLoc
      ? addrLineList({
          street: location?.street,
          city: location?.city,
          state: location?.state,
          zip: location?.zip,
        })
      : [];

    let billY = y;
    let locY = y;
    if (billLines.length === 0) {
      doc.setFont("helvetica", "italic");
      doc.setTextColor(136, 136, 136);
      doc.text("No billing address on file", MARGIN, billY);
      doc.setFont("helvetica", "normal");
      doc.setTextColor(34, 34, 34);
      billY += 12;
    } else {
      for (const line of billLines) {
        doc.text(line, MARGIN, billY);
        billY += 12;
      }
    }
    for (const line of locLines) {
      doc.text(line, locColX, locY);
      locY += 12;
    }

    y = Math.max(billY, locY, partyStartY + 38) + 8;

    // --- Title + scope + exclusions ---
    // Renders any multi-line free-text block as a bullet list: each
    // newline-separated item becomes one "• " bullet; continuation
    // wrap lines indent under the bullet. Justin's ask 2026-06-12 —
    // scope / exclusions / terms behave like a Word bullet list now.
    const renderBulletBlock = (label, text) => {
      const items = (text || "")
        .split(/\r?\n/)
        .map((s) => s.trim())
        .filter(Boolean);
      if (items.length === 0) return;
      doc.setFont("helvetica", "bold");
      doc.setFontSize(11);
      doc.text(`${label}:`, MARGIN, y);
      y += 14;
      doc.setFont("helvetica", "normal");
      doc.setFontSize(10);
      for (const item of items) {
        const wrapped = doc.splitTextToSize(item, CONTENT_W - 16);
        doc.text(`• ${wrapped[0]}`, MARGIN, y);
        y += 12;
        for (let i = 1; i < wrapped.length; i++) {
          doc.text(`  ${wrapped[i]}`, MARGIN + 12, y);
          y += 12;
        }
      }
      y += 4;
      doc.setFontSize(11);
    };

    if (quote.title) {
      doc.setFontSize(11);
      doc.text(quote.title, MARGIN, y);
      y += 14;
    }
    renderBulletBlock("Scope of work", quote.scope);
    renderBulletBlock("Exclusions", quote.exclusions);

    // --- Maintenance contract terms (PDF) ---
    // Renders just below scope/exclusions so the customer sees the term
    // commitment + visit schedule before the price block. Skipped entirely
    // on one-time job quotes — keeps that PDF identical to before.
    if (isMaintenance) {
      doc.setFont("helvetica", "bold");
      doc.setFontSize(11);
      doc.text("Contract terms:", MARGIN, y);
      y += 14;
      doc.setFont("helvetica", "normal");
      doc.setFontSize(10);
      const escalationPct = num(quote.contractAnnualEscalationPct) || 0;
      const termLines = [
        `Term: ${contractTermY} year${contractTermY === 1 ? "" : "s"}`,
        `Start date: ${quote.contractStartDate || "(not set)"}`,
        `Visit interval: ${visitIntervalLabel} (${visitsPerYear}/yr)`,
        `Total visits over term: ${totalVisits}`,
        `Billing: ${billingModeLabel}`,
        ...(escalationPct > 0
          ? [`Annual price escalator: ${escalationPct}% per year`]
          : []),
      ];
      for (const line of termLines) {
        doc.text(`- ${line}`, MARGIN, y);
        y += 12;
      }
      y += 4;
      doc.setFontSize(11);
    }

    // --- Line items table ---
    // summary:   subtotal + tax/fee lines + total (line items hidden)
    // breakdown: every labor/material/charge + tax/fee lines + total
    //
    // Tax & fee lines render in BOTH formats. Sales tax must be separately
    // stated on every customer-facing money document (TX comptroller +
    // most-state norm + customer-clarity rule). When the customer is
    // tax-exempt, the per-tax-line list is replaced with a single
    // "EXEMPT" row citing the cert # — the block stays visible (never
    // silently disappears) but communicates the zero amount + reason.
    const taxRows = customerExempt
      ? [
          [
            `Sales tax — EXEMPT${
              customer?.taxExemptCert
                ? ` (Cert ${customer.taxExemptCert})`
                : ""
            }`,
            money(0),
          ],
        ]
      : (c.taxes || []).map((t) => {
          const amt =
            t.mode === "flat"
              ? num(t.amount)
              : taxableSubtotal * (num(t.rate) / 100);
          return [
            `${t.name || "Tax/fee"}${
              t.mode === "percent" && num(t.rate) ? ` (${num(t.rate)}%)` : ""
            }`,
            money(amt),
          ];
        });

    const body = [];
    if (format === "breakdown") {
      (c.labor || []).forEach((l) =>
        body.push([
          l.description || "Labor",
          money(num(l.hours) * num(l.price)),
        ])
      );
      (c.materials || []).forEach((m) =>
        body.push([
          `${m.name || "Material"}${m.partNumber ? ` (#${m.partNumber})` : ""}`,
          money(lineWithMarkup(m)),
        ])
      );
      (c.charges || []).forEach((x) =>
        body.push([x.description || "Charge", money(lineWithMarkup(x))])
      );
      (c.fuel || []).forEach((f) => {
        const label =
          f.mode === "flat"
            ? f.description || "Trip charge"
            : `Fuel / trip (${num(f.trips)} trip${num(f.trips) === 1 ? "" : "s"}, ${num(f.milesOneWay)} mi 1-way)`;
        body.push([label, money(fuelLineWithMarkup(f))]);
      });
      body.push(...taxRows);
    } else {
      // summary: line items collapsed into one Subtotal row, but tax/fee
      // lines stay visible — customer needs to see the tax breakdown on
      // the quote they're signing.
      body.push(["Subtotal", money(priceSubtotal)]);
      body.push(...taxRows);
    }

    autoTable(doc, {
      startY: y,
      head: [["Description", "Amount"]],
      body,
      foot: [
        [
          {
            content: isMaintenance ? "Annual Total" : "Total",
            styles: { fontSize: 13 },
          },
          { content: money(grandTotal), styles: { halign: "right", fontSize: 13 } },
        ],
      ],
      theme: "plain",
      // top margin used on continuation pages (leaves room for the
      // mini-header stampPageHeaders draws at the end). bottom margin
      // keeps content off the org footer.
      margin: {
        left: MARGIN,
        right: MARGIN,
        top: QUOTE_TOP_MARGIN_PAGE2,
        bottom: QUOTE_BOTTOM_MARGIN,
      },
      styles: { fontSize: 11, cellPadding: 6, overflow: "linebreak" },
      headStyles: {
        fontStyle: "bold",
        fillColor: false,
        textColor: 34,
        lineWidth: { bottom: 0.75 },
        lineColor: 200,
      },
      footStyles: {
        fontStyle: "bold",
        fillColor: false,
        textColor: 34,
        lineWidth: { top: 1 },
        lineColor: 80,
      },
      columnStyles: {
        0: { cellWidth: "auto" },
        1: { cellWidth: 110, halign: "right" },
      },
    });

    y = doc.lastAutoTable.finalY + 14;

    // Lifetime value callout — only shows on multi-year maintenance quotes.
    // The annual line above is what the customer pays each year (year 1
    // when an escalator is set); this line makes the total commitment
    // visible so there are no surprises.
    if (isMaintenance && contractTermY > 1) {
      y = ensureQuoteSpace(doc, y, 24);
      doc.setFont("helvetica", "bold");
      doc.setFontSize(11);
      doc.setTextColor(26, 95, 180);
      const escalationPct = num(quote.contractAnnualEscalationPct) || 0;
      let lifetimeTotal = grandTotal * contractTermY;
      if (escalationPct > 0) {
        lifetimeTotal = 0;
        for (let y2 = 1; y2 <= contractTermY; y2++) {
          lifetimeTotal +=
            grandTotal * Math.pow(1 + escalationPct / 100, y2 - 1);
        }
      }
      const lifetimeLine = escalationPct > 0
        ? `Lifetime contract value with ${escalationPct}% escalator: ${money(
            lifetimeTotal
          )} (${contractTermY} yr)`
        : `Lifetime contract value: ${money(lifetimeTotal)} (${contractTermY} yr x annual)`;
      doc.text(lifetimeLine, MARGIN, y);
      y += 16;
      doc.setTextColor(85, 85, 85);
    }

    // --- Validity + terms ---
    y = ensureQuoteSpace(doc, y, 30);
    doc.setFont("helvetica", "normal");
    doc.setFontSize(10);
    doc.setTextColor(85, 85, 85);
    const validityLine = `Quote valid for ${quote.validDays ?? 30} days${
      expiry ? ` (expires ${expiry})` : ""
    }.`;
    doc.text(validityLine, MARGIN, y);
    y += 14;
    if (quote.terms) {
      doc.setFontSize(11);
      doc.setTextColor(34, 34, 34);
      renderBulletBlock("Terms", quote.terms);
    }

    // --- Signature block: contractor sig + customer ink line ---
    // Reserve roughly: sig label (20) + sig image area (60) + gap (36)
    // + customer line + label (30) = ~145pt. Keep-together so a dropped
    // page doesn't show a half-signature.
    y = ensureQuoteSpace(doc, y + 12, 150);
    doc.setFont("helvetica", "bold");
    doc.setFontSize(11);
    doc.setTextColor(34, 34, 34);
    const sigLabel = `Authorized by (contractor): ${sig?.name || ""}${
      sig?.signedAt ? ` — ${new Date(sig.signedAt).toLocaleDateString()}` : ""
    }`;
    doc.text(sigLabel, MARGIN, y);
    y += 6;
    if (sig?.image) {
      try {
        doc.addImage(sig.image, "PNG", MARGIN, y, 180, 50);
        y += 56;
      } catch (err) {
        console.warn("contractor sig addImage failed:", err);
      }
    }
    y += 36;
    doc.setDrawColor(51, 51, 51);
    doc.line(MARGIN, y, MARGIN + 240, y);
    doc.setFont("helvetica", "normal");
    doc.setFontSize(10);
    doc.text("Customer signature", MARGIN, y + 12);
    doc.line(MARGIN + 260, y, MARGIN + 260 + 140, y);
    doc.text("Date", MARGIN + 260, y + 12);

    // Stamp per-page identification headers AFTER everything is drawn
    // so the page count is final and pages 2+ all get their mini-
    // header with Page X of Y.
    stampPageHeaders(doc, {
      logoData,
      orgName,
      quoteNumber: quote.quoteNumber,
      isMaintenance,
      revision: quote.revision || 0,
    });

    // Org-level centered footer (license #, "Thank you", insurance ref,
    // etc.) -- comes from CompanySettingsPage Quote & Bill Defaults.
    stampCenteredFooter(doc, quoteDefaults?.footer);

    const quoteSlug = quote.quoteNumber || quote.id.slice(0, 6);
    const dateSlug = new Date().toISOString().slice(0, 10);
    const filename = `Quote_${quoteSlug}_${dateSlug}.pdf`;
    if (shouldShowPdfChooser()) {
      setPdfPending({ doc, filename });
    } else {
      await exportPdf(doc, filename);
    }
  };

  // Internal "Office Copy" quote print — admin/manager only via canSeeMargin.
  // Shows every line's COST vs CHARGE vs MARGIN so an owner/manager can
  // review pricing before sending the customer-facing quote. Watermarked
  // OFFICE COPY — NOT FOR CUSTOMER so an accidental handoff is obvious.
  // Per project_internal_itemized_quote memory (Justin's ask after he
  // couldn't show his boss an itemized quote to validate pricing).
  const printOfficeCopy = async () => {
    const linkedJob = quote.jobId
      ? jobsRaw.find((j) => j.id === quote.jobId)
      : null;
    // Office Copy uses ASCII-only chars in autoTable cells + doc.text
    // strings (no em-dashes, no Unicode minus, no division sign) — jsPDF's
    // default Helvetica encoding garbles those glyphs character-by-
    // character inside autoTable bodies (Justin saw "G r o s s ... " and
    // strings with &&&&& between letters). The customer-facing printQuote
    // gets away with em-dashes because it uses them mostly outside autoTable.
    const metaLabel = linkedJob
      ? `Job #${linkedJob.jobNumber || "-"}${
          linkedJob.title ? ` - ${linkedJob.title}` : ""
        }`
      : `Quote #${quote.quoteNumber || "-"}`;

    const addrLineList = (addr = {}) => {
      const lines = [];
      if (addr.street) lines.push(addr.street);
      const cityLine = [addr.city, addr.state, addr.zip]
        .filter(Boolean)
        .join(", ");
      if (cityLine) lines.push(cityLine);
      if (addr.phone) lines.push(addr.phone);
      return lines;
    };

    const doc = new jsPDF({ unit: "pt", format: "letter" });
    const PAGE_W = doc.internal.pageSize.getWidth();
    const MARGIN = 40;
    const CONTENT_W = PAGE_W - MARGIN * 2;

    // --- Watermark banner across the top (visible on every page) ---
    doc.setFillColor(255, 235, 235);
    doc.rect(0, 0, PAGE_W, 28, "F");
    doc.setFont("helvetica", "bold");
    doc.setFontSize(11);
    doc.setTextColor(150, 30, 30);
    doc.text(
      "OFFICE COPY - NOT FOR CUSTOMER - INTERNAL PRICING REVIEW",
      MARGIN,
      18
    );

    // --- Header (logo + brand) ---
    const logoData = await loadImageAsDataUrl(logoUrl);
    let titleX = MARGIN;
    const headerTop = MARGIN + 14;
    if (logoData) {
      try {
        doc.addImage(logoData, "AUTO", MARGIN, headerTop, 80, 60);
        titleX = MARGIN + 90;
      } catch (err) {
        console.warn("office copy: logo addImage failed:", err);
      }
    }
    doc.setFont("helvetica", "bold");
    doc.setFontSize(18);
    doc.setTextColor(34, 34, 34);
    const officeRevTag =
      (quote.revision || 0) > 0 ? ` Rev ${quote.revision}` : "";
    doc.text(
      `Quote ${quote.quoteNumber || ""}${officeRevTag} - Office Copy`.trim(),
      titleX,
      headerTop + 18
    );

    let headerY = headerTop + 18;
    if (orgName) {
      doc.setFont("helvetica", "normal");
      doc.setFontSize(11);
      doc.setTextColor(102, 102, 102);
      headerY += 16;
      doc.text(orgName, titleX, headerY);
    }
    doc.setFontSize(10);
    doc.setTextColor(85, 85, 85);
    for (const line of addrLineList(orgAddress)) {
      headerY += 12;
      doc.text(line, titleX, headerY);
    }
    headerY = Math.max(headerY, headerTop + 60);

    // --- Doc meta + date ---
    let y = headerY + 28;
    doc.setFont("helvetica", "bold");
    doc.setFontSize(14);
    doc.setTextColor(26, 95, 180);
    doc.text(metaLabel, MARGIN, y);
    doc.setFont("helvetica", "normal");
    doc.setFontSize(11);
    doc.setTextColor(34, 34, 34);
    const dateStr = `Date: ${new Date().toLocaleDateString()}`;
    doc.text(dateStr, PAGE_W - MARGIN - doc.getTextWidth(dateStr), y);

    // --- Parties (single line each - office copy doesn't need the
    // detailed billing-address block; pricing review is the focus) ---
    y += 22;
    doc.setFontSize(11);
    doc.setFont("helvetica", "bold");
    doc.text("Customer:", MARGIN, y);
    doc.setFont("helvetica", "normal");
    doc.text(customer?.name || customerName || "-", MARGIN + 70, y);
    if (quote.locationId && (location?.name || locationName)) {
      y += 14;
      doc.setFont("helvetica", "bold");
      doc.text("Location:", MARGIN, y);
      doc.setFont("helvetica", "normal");
      doc.text(location?.name || locationName || "-", MARGIN + 70, y);
    }
    y += 20;

    if (quote.title) {
      doc.setFont("helvetica", "bold");
      doc.setFontSize(11);
      doc.text("Title:", MARGIN, y);
      doc.setFont("helvetica", "normal");
      doc.text(quote.title, MARGIN + 70, y);
      y += 18;
    }
    // Bullet renderer for scope / exclusions / terms — matches the
    // customer-facing print so the office copy shows the same itemized
    // structure. Uses ASCII "-" so jsPDF Helvetica encoding stays clean
    // (bullets in doc.text are fine in autoTable bodies they'd garble).
    const renderOfficeBulletBlock = (label, text) => {
      const items = (text || "")
        .split(/\r?\n/)
        .map((s) => s.trim())
        .filter(Boolean);
      if (items.length === 0) return;
      doc.setFont("helvetica", "bold");
      doc.setFontSize(11);
      doc.text(`${label}:`, MARGIN, y);
      y += 14;
      doc.setFont("helvetica", "normal");
      doc.setFontSize(10);
      for (const item of items) {
        const wrapped = doc.splitTextToSize(item, CONTENT_W - 16);
        doc.text(`- ${wrapped[0]}`, MARGIN, y);
        y += 12;
        for (let i = 1; i < wrapped.length; i++) {
          doc.text(`  ${wrapped[i]}`, MARGIN + 12, y);
          y += 12;
        }
      }
      y += 4;
      doc.setFontSize(11);
    };

    renderOfficeBulletBlock("Scope", quote.scope);
    renderOfficeBulletBlock("Exclusions", quote.exclusions);

    // --- Internal pricing table ---
    // Columns: Description / Detail / Cost / Charge / Margin
    // Labor cost = hrs × cost rate. Charge = hrs × charge rate. Margin = $.
    // Materials/charges cost = qty × unit cost. Charge = cost × (1+markup%).
    const body = [];
    let totalCost = 0;
    let totalCharge = 0;

    (c.labor || []).forEach((l) => {
      const hrs = num(l.hours);
      const cost = hrs * num(l.cost);
      const charge = hrs * num(l.price);
      totalCost += cost;
      totalCharge += charge;
      body.push([
        `Labor${l.description ? " - " + l.description : ""}`,
        `${hrs} hr @ ${money(num(l.cost))} cost / ${money(num(l.price))} charge`,
        money(cost),
        money(charge),
        money(charge - cost),
      ]);
    });

    (c.materials || []).forEach((m) => {
      const qty = num(m.qty);
      const unitCost = num(m.cost);
      const markup = num(m.markup);
      const cost = qty * unitCost;
      const charge = cost * (1 + markup / 100);
      totalCost += cost;
      totalCharge += charge;
      body.push([
        `${m.name || "Material"}${m.partNumber ? ` (#${m.partNumber})` : ""}`,
        `${qty} @ ${money(unitCost)} + ${markup}% markup`,
        money(cost),
        money(charge),
        money(charge - cost),
      ]);
    });

    (c.charges || []).forEach((x) => {
      const qty = num(x.qty);
      const unitCost = num(x.cost);
      const markup = num(x.markup);
      const cost = qty * unitCost;
      const charge = cost * (1 + markup / 100);
      totalCost += cost;
      totalCharge += charge;
      body.push([
        x.description || "Charge",
        `${qty} @ ${money(unitCost)} + ${markup}% markup`,
        money(cost),
        money(charge),
        money(charge - cost),
      ]);
    });

    // Fuel / Trip lines. Mileage-mode rows show miles + trips in the
    // detail column; flat-mode rows show the flat amount. Cost vs charge
    // vs margin columns stay the same as every other line so the office
    // copy reviewer can scan the whole table consistently.
    (c.fuel || []).forEach((f) => {
      const markup = num(f.markup);
      const cost = fuelLineCost(f);
      const charge = fuelLineWithMarkup(f);
      totalCost += cost;
      totalCharge += charge;
      const label =
        f.mode === "flat"
          ? f.description || "Trip charge"
          : `Fuel / trip (mileage)`;
      const detail =
        f.mode === "flat"
          ? `flat ${money(num(f.amount))} + ${markup}% markup`
          : `${num(f.trips)} trip(s) x ${num(f.milesOneWay)} mi 1-way @ ${money(num(f.costPerMile))}/mi + ${markup}% markup`;
      body.push([label, detail, money(cost), money(charge), money(charge - cost)]);
    });

    // Tax + fee lines apply to the customer-facing charge total. Shown
    // here for completeness — they're customer-side numbers but the
    // office copy reviewer needs them when reconciling against the
    // customer-facing quote total.
    const fuelTaxableLocal = (c.fuel || []).reduce(
      (s, f) => (f.taxable !== false ? s + fuelLineWithMarkup(f) : s),
      0
    );
    const taxableSubtotalLocal = fuelTaxableLocal + (c.labor || []).reduce(
      (s, l) =>
        l.taxable !== false ? s + num(l.hours) * num(l.price) : s,
      0
    ) +
    (c.materials || []).reduce(
      (s, m) =>
        m.taxable !== false
          ? s + num(m.qty) * num(m.cost) * (1 + num(m.markup) / 100)
          : s,
      0
    ) +
    (c.charges || []).reduce(
      (s, x) =>
        x.taxable !== false
          ? s + num(x.qty) * num(x.cost) * (1 + num(x.markup) / 100)
          : s,
      0
    );
    let taxTotalLocal = 0;
    (c.taxes || []).forEach((t) => {
      const amt = customerExempt
        ? 0
        : t.mode === "flat"
        ? num(t.amount)
        : taxableSubtotalLocal * (num(t.rate) / 100);
      taxTotalLocal += amt;
      body.push([
        customerExempt
          ? `Sales tax - EXEMPT${
              customer?.taxExemptCert
                ? ` (Cert ${customer.taxExemptCert})`
                : ""
            }`
          : `${t.name || "Tax/fee"}${
              t.mode === "percent" && num(t.rate)
                ? ` (${num(t.rate)}%)`
                : ""
            }`,
        t.mode === "flat" ? "flat fee" : "% of taxable subtotal",
        "-",
        money(amt),
        "-",
      ]);
    });

    autoTable(doc, {
      startY: y,
      head: [["Description", "Detail", "Cost", "Charge", "Margin"]],
      body,
      foot: [
        [
          { content: "Totals (pre-tax)", colSpan: 2 },
          { content: money(totalCost), styles: { halign: "right" } },
          { content: money(totalCharge), styles: { halign: "right" } },
          {
            content: money(totalCharge - totalCost),
            styles: { halign: "right" },
          },
        ],
      ],
      theme: "plain",
      margin: {
        left: MARGIN,
        right: MARGIN,
        top: QUOTE_TOP_MARGIN_PAGE2,
        bottom: QUOTE_BOTTOM_MARGIN,
      },
      styles: { fontSize: 10, cellPadding: 5, overflow: "linebreak" },
      headStyles: {
        fontStyle: "bold",
        fillColor: false,
        textColor: 34,
        lineWidth: { bottom: 0.75 },
        lineColor: 200,
      },
      bodyStyles: {
        lineWidth: { bottom: 0.4 },
        lineColor: 215,
      },
      footStyles: {
        fontStyle: "bold",
        fillColor: false,
        textColor: 34,
        lineWidth: { top: 1.25 },
        lineColor: 80,
      },
      columnStyles: {
        0: { cellWidth: "auto" },
        1: { cellWidth: 130 },
        2: { cellWidth: 70, halign: "right" },
        3: { cellWidth: 70, halign: "right" },
        4: { cellWidth: 70, halign: "right" },
      },
    });

    // --- Gross margin summary block (the headline number for the
    // reviewer — margin %, markup %, customer total including tax) ---
    const grossProfit = totalCharge - totalCost;
    const grossMarginPct =
      totalCharge > 0 ? (grossProfit / totalCharge) * 100 : 0;
    const grossMarkupPct =
      totalCost > 0 ? (grossProfit / totalCost) * 100 : 0;
    const customerTotal = totalCharge + taxTotalLocal;

    autoTable(doc, {
      startY: doc.lastAutoTable.finalY + 12,
      theme: "plain",
      margin: {
        left: MARGIN,
        right: MARGIN,
        top: QUOTE_TOP_MARGIN_PAGE2,
        bottom: QUOTE_BOTTOM_MARGIN,
      },
      body: [
        [
          { content: "Total cost (internal)" },
          { content: money(totalCost), styles: { halign: "right" } },
        ],
        [
          { content: "Total charge (pre-tax)" },
          { content: money(totalCharge), styles: { halign: "right" } },
        ],
        [
          {
            content: "Gross profit (charge - cost)",
            styles: { fontStyle: "bold" },
          },
          {
            content: money(grossProfit),
            styles: { halign: "right", fontStyle: "bold" },
          },
        ],
        [
          { content: "Margin % (profit / charge)" },
          {
            content: `${grossMarginPct.toFixed(1)}%`,
            styles: { halign: "right" },
          },
        ],
        [
          { content: "Markup % (profit / cost)" },
          {
            content: `${grossMarkupPct.toFixed(1)}%`,
            styles: { halign: "right" },
          },
        ],
        [
          { content: "Taxes & fees" },
          { content: money(taxTotalLocal), styles: { halign: "right" } },
        ],
        [
          {
            content: "Customer total (the number on the customer quote)",
            styles: { fontStyle: "bold", fontSize: 12 },
          },
          {
            content: money(customerTotal),
            styles: { halign: "right", fontStyle: "bold", fontSize: 12 },
          },
        ],
      ],
      styles: { fontSize: 10, cellPadding: 5 },
      bodyStyles: {
        lineWidth: { bottom: 0.4 },
        lineColor: 215,
      },
      columnStyles: { 1: { halign: "right" } },
    });

    // Terms — same bullet renderer, after the pricing tables.
    y = doc.lastAutoTable.finalY + 16;
    y = ensureQuoteSpace(doc, y, 60);
    doc.setTextColor(34, 34, 34);
    renderOfficeBulletBlock("Terms", quote.terms);

    // --- Footer watermark on the last page ---
    let footY = Math.max(doc.lastAutoTable.finalY + 24, y + 12);
    footY = ensureQuoteSpace(doc, footY, 20);
    doc.setFont("helvetica", "italic");
    doc.setFontSize(10);
    doc.setTextColor(150, 30, 30);
    doc.text(
      "OFFICE COPY - internal pricing review. Do not send to customer.",
      MARGIN,
      footY
    );

    // Stamp the same org footer on the office copy so admin/owner
    // reviewing the pricing sees the same layout the customer will get.
    stampCenteredFooter(doc, quoteDefaults?.footer);

    // Per-page identification headers (logo + org + Quote # + Page X of Y)
    // on every page 2+ so dropped pages stay identifiable. Tagged
    // "Office Copy" so an admin can tell at a glance which version a
    // loose page belongs to.
    stampPageHeaders(doc, {
      logoData,
      orgName,
      quoteNumber: quote.quoteNumber,
      isMaintenance,
      copyTag: "Office Copy",
      revision: quote.revision || 0,
    });

    const quoteSlug = quote.quoteNumber || quote.id.slice(0, 6);
    const dateSlug = new Date().toISOString().slice(0, 10);
    const filename = `Quote_${quoteSlug}_OFFICE-COPY_${dateSlug}.pdf`;
    if (shouldShowPdfChooser()) {
      setPdfPending({ doc, filename });
    } else {
      await exportPdf(doc, filename);
    }
  };

  return (
    <>
      <BackButton onClick={onBack} />
      <h2 style={{ marginBottom: "2px" }}>
        {quote.quoteNumber ? `${quote.quoteNumber} — ` : ""}
        {quote.title || (isMaintenance ? "Maintenance Quote" : "Quote")}
        {(quote.revision || 0) > 0 && (
          <span
            style={{
              marginLeft: "8px",
              padding: "2px 8px",
              borderRadius: "4px",
              background: "#fff3cd",
              color: "#9a6b00",
              fontSize: "13px",
              fontWeight: 700,
              verticalAlign: "middle",
            }}
          >
            Rev {quote.revision}
          </span>
        )}
      </h2>
      <div style={{ fontSize: "13px", color: "#666", marginBottom: "10px" }}>
        {customerName || "—"}
        {locationName ? ` / ${locationName}` : ""} • Status:{" "}
        <strong>
          {quote.status === "sent" ? "Sent — out for approval" : quote.status}
        </strong>
        {isMaintenance && (
          <span
            style={{
              marginLeft: "8px",
              padding: "1px 6px",
              borderRadius: "4px",
              background: "#e0eaff",
              color: "#1a5fb4",
              fontSize: "11px",
              fontWeight: 600,
            }}
          >
            PM CONTRACT
          </span>
        )}
      </div>

      <div style={styles.card}>
        {(() => {
          const renderBulletDiv = (label, text) => {
            const items = (text || "")
              .split(/\r?\n/)
              .map((s) => s.trim())
              .filter(Boolean);
            if (items.length === 0) return null;
            return (
              <div style={{ fontSize: "13px", marginTop: "4px" }}>
                <strong>{label}:</strong>
                <ul style={{ margin: "2px 0", paddingLeft: "20px" }}>
                  {items.map((item, i) => (
                    <li key={i}>{item}</li>
                  ))}
                </ul>
              </div>
            );
          };
          return (
            <>
              {renderBulletDiv("Scope of work", quote.scope)}
              {renderBulletDiv("Exclusions", quote.exclusions)}
            </>
          );
        })()}

        {isMaintenance && (
          <div
            style={{
              marginTop: "8px",
              padding: "8px 10px",
              background: "#f3f7ff",
              border: "1px solid #cfdcf5",
              borderRadius: "6px",
              fontSize: "13px",
            }}
          >
            <strong>Contract terms</strong>
            <div style={{ marginTop: "4px", fontSize: "12px" }}>
              Term: <strong>{contractTermY}</strong> year
              {contractTermY === 1 ? "" : "s"} • Visits:{" "}
              <strong>
                {visitIntervalLabel} ({visitsPerYear}/yr)
              </strong>{" "}
              • Total visits over term: <strong>{totalVisits}</strong>
            </div>
            <div style={{ fontSize: "12px" }}>
              Start date:{" "}
              <strong>{quote.contractStartDate || "(not set)"}</strong> •
              Billing: <strong>{billingModeLabel}</strong>
            </div>
            {num(quote.contractAnnualEscalationPct) > 0 && (
              <div style={{ fontSize: "12px", marginTop: "2px" }}>
                Annual price escalator:{" "}
                <strong>
                  {num(quote.contractAnnualEscalationPct)}% per year
                </strong>
              </div>
            )}
          </div>
        )}

        {(c.labor || []).length > 0 && (
          <div style={{ fontSize: "13px", marginTop: "6px" }}>
            <strong>Labor</strong>
            <ul style={{ margin: "2px 0", paddingLeft: "18px" }}>
              {c.labor.map((l) => (
                <li key={l.id}>
                  {l.description || "Labor"} — {num(l.hours)} hr @{" "}
                  {money(num(l.price))} = {money(num(l.hours) * num(l.price))}
                </li>
              ))}
            </ul>
          </div>
        )}

        {(c.materials || []).length > 0 && (
          <div style={{ fontSize: "13px", marginTop: "6px" }}>
            <strong>Materials</strong>
            <ul style={{ margin: "2px 0", paddingLeft: "18px" }}>
              {c.materials.map((m) => (
                <li key={m.id}>
                  {m.name || "Material"}
                  {m.partNumber ? ` (#${m.partNumber})` : ""} — qty {num(m.qty)} ={" "}
                  {money(lineWithMarkup(m))}
                </li>
              ))}
            </ul>
          </div>
        )}

        {(c.charges || []).length > 0 && (
          <div style={{ fontSize: "13px", marginTop: "6px" }}>
            <strong>Other Charges</strong>
            <ul style={{ margin: "2px 0", paddingLeft: "18px" }}>
              {c.charges.map((x) => (
                <li key={x.id}>
                  {x.description || "Charge"} — {money(lineWithMarkup(x))}
                </li>
              ))}
            </ul>
          </div>
        )}

        {(c.fuel || []).length > 0 && (
          <div style={{ fontSize: "13px", marginTop: "6px" }}>
            <strong>Fuel / Trip</strong>
            <ul style={{ margin: "2px 0", paddingLeft: "18px" }}>
              {c.fuel.map((f) => (
                <li key={f.id}>
                  {f.mode === "flat"
                    ? `${f.description || "Trip charge"}`
                    : `Fuel / trip — ${num(f.trips)} trip${
                        num(f.trips) === 1 ? "" : "s"
                      }, ${num(f.milesOneWay)} mi 1-way @ ${money(
                        num(f.costPerMile)
                      )}/mi`}{" "}
                  = {money(fuelLineWithMarkup(f))}
                </li>
              ))}
            </ul>
          </div>
        )}

        <div
          style={{
            marginTop: "10px",
            paddingTop: "8px",
            borderTop: "1px solid #ddd",
            fontSize: "14px",
          }}
        >
          <div>Subtotal: {money(priceSubtotal)}</div>
          {customerExempt ? (
            <div style={{ fontSize: "12px", color: "#6a4900" }}>
              Sales tax — EXEMPT
              {customer?.taxExemptCert ? ` (Cert ${customer.taxExemptCert})` : ""}:{" "}
              {money(0)}
            </div>
          ) : (
            (c.taxes || []).map((t) => (
              <div key={t.id} style={{ fontSize: "12px", color: "#666" }}>
                {t.name || "Tax/fee"}
                {t.mode === "percent" && num(t.rate)
                  ? ` (${num(t.rate)}%)`
                  : ""}:{" "}
                {money(
                  t.mode === "flat"
                    ? num(t.amount)
                    : taxableSubtotal * (num(t.rate) / 100)
                )}
              </div>
            ))
          )}
          <div style={{ fontWeight: "bold", fontSize: "16px" }}>
            {isMaintenance ? "Annual quote total: " : "Quote total: "}
            {money(grandTotal)}
          </div>
          {isMaintenance && contractTermY > 1 && (() => {
            const escPct = num(quote.contractAnnualEscalationPct) || 0;
            let lifetime = grandTotal * contractTermY;
            if (escPct > 0) {
              lifetime = 0;
              for (let y2 = 1; y2 <= contractTermY; y2++) {
                lifetime += grandTotal * Math.pow(1 + escPct / 100, y2 - 1);
              }
            }
            return (
              <div style={{ fontSize: "13px", color: "#1a5fb4" }}>
                Lifetime contract value
                {escPct > 0 ? ` with ${escPct}% escalator` : ""}:{" "}
                {money(lifetime)} ({contractTermY} yr
                {escPct === 0 ? " × annual" : ""})
              </div>
            );
          })()}
          {canSeeMargin && (
            <div style={{ marginTop: "4px", fontSize: "13px", color: "#666" }}>
              Cost: {money(totalCost)} • Gross profit: {money(margin)} • Margin:{" "}
              {marginPct.toFixed(1)}% • Markup: {markupPct.toFixed(1)}%
            </div>
          )}
        </div>

        <div style={{ fontSize: "12px", color: "#666", marginTop: "10px" }}>
          Quote valid for {quote.validDays ?? 30} days
          {expiry ? ` — expires ${expiry}` : ""}.
        </div>
        {(() => {
          const items = (quote.terms || "")
            .split(/\r?\n/)
            .map((s) => s.trim())
            .filter(Boolean);
          if (items.length === 0) return null;
          return (
            <div style={{ fontSize: "12px", marginTop: "4px" }}>
              <strong>Terms:</strong>
              <ul style={{ margin: "2px 0", paddingLeft: "20px" }}>
                {items.map((item, i) => (
                  <li key={i}>{item}</li>
                ))}
              </ul>
            </div>
          );
        })()}

        {sig && (
          <div style={{ marginTop: "10px", fontSize: "12px" }}>
            <strong>Contractor signature:</strong> {sig.name}
            {sig.signedAt
              ? ` — ${new Date(sig.signedAt).toLocaleDateString()}`
              : ""}
            {sig.image && (
              <div>
                <img
                  src={sig.image}
                  alt="contractor signature"
                  style={{ borderBottom: "1px solid #333", maxWidth: "260px" }}
                />
              </div>
            )}
          </div>
        )}

        <div style={{ marginTop: "12px" }}>
          {isDraft && canSend && (
            <>
              <button style={styles.button} onClick={onEdit}>
                Edit
              </button>
              <button
                style={{ ...styles.button, background: "#1a5fb4", color: "white" }}
                onClick={send}
                disabled={sending}
              >
                {sending ? "Sending..." : "Send to Customer"}
              </button>
              {!sig && (
                <div style={{ fontSize: "12px", color: "#a15c00", marginTop: "4px" }}>
                  Sign the quote in Edit before sending.
                </div>
              )}
            </>
          )}
          {!isDraft && (
            <>
              <label style={{ fontSize: "13px" }}>Quote format: </label>
              <select
                value={quoteFormat}
                onChange={(e) => setQuoteFormat(e.target.value)}
              >
                <option value="summary">Summary (total only)</option>
                <option value="breakdown">Breakdown (line items)</option>
                {canSeeMargin && (
                  <option value="office">
                    Itemized Office Copy (internal — NOT for customer)
                  </option>
                )}
              </select>{" "}
              <button
                style={styles.button}
                onClick={() =>
                  quoteFormat === "office"
                    ? printOfficeCopy()
                    : printQuote(quoteFormat)
                }
                title={
                  quoteFormat === "office"
                    ? "Internal office copy - cost vs charge vs margin. NOT for the customer."
                    : "Customer-facing quote export"
                }
              >
                Export
              </button>
            </>
          )}
          {quote.status === "sent" && canMarkApproved && (
            <button
              style={{ ...styles.button, background: "#1a7f37", color: "white" }}
              onClick={markApproved}
              disabled={approvingJob}
            >
              {approvingJob ? "Approving..." : "Mark Approved"}
            </button>
          )}
          {quote.status === "sent" && canSend && (
            <button
              style={{ ...styles.button, background: "#fff8e1", color: "#9a6b00" }}
              onClick={handleAmend}
              disabled={amending}
              title="Save the current version to revision history and re-open the editor. Customer sees Rev N on the new PDF."
            >
              {amending ? "Amending..." : "Amend"}
            </button>
          )}
          {quote.status === "approved" && (
            <span style={{ fontSize: "13px", color: "#1a7f37", marginLeft: "6px" }}>
              Approved &amp; locked —{" "}
              {isMaintenance ? "contract created." : "job created."}
            </span>
          )}
        </div>
      </div>

      {/* Revision history -- prior versions of the quote, kept on amend.
          Each row is clickable: opens a modal with the full snapshot of
          that revision PLUS a diff vs the current state ("what changed
          since this version"). Phase 1 = view only; re-print of a
          prior revision as PDF is future scope. */}
      {(quote.revisionHistory || []).length > 0 && (
        <div style={styles.card}>
          <strong style={{ fontSize: "13px" }}>
            Revision history ({(quote.revisionHistory || []).length})
          </strong>
          <div
            style={{
              fontSize: "11px",
              color: "#888",
              margin: "2px 0 8px",
            }}
          >
            Click a row to view what that version looked like.
          </div>
          <div style={{ display: "flex", flexDirection: "column", gap: "4px" }}>
            {(quote.revisionHistory || []).map((rev, i) => {
              const sigName = rev.contractorSignature?.name || "(unsigned)";
              const sentAt = rev.sentAt
                ? new Date(rev.sentAt).toLocaleDateString()
                : "(never sent)";
              return (
                <button
                  key={i}
                  type="button"
                  onClick={() => setViewingRevision(rev)}
                  style={{
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "space-between",
                    gap: "8px",
                    padding: "8px 10px",
                    background: "#f6f8fb",
                    border: "1px solid #e3e7eb",
                    borderRadius: "6px",
                    cursor: "pointer",
                    textAlign: "left",
                    fontSize: "12px",
                    color: "#222",
                    fontFamily: "inherit",
                  }}
                >
                  <span>
                    <strong style={{ color: "#1a5fb4" }}>
                      Rev {rev.revision || 0}
                    </strong>{" "}
                    — sent {sentAt} — signed by {sigName}
                  </span>
                  <span style={{ color: "#1a5fb4", fontSize: "11px" }}>
                    View →
                  </span>
                </button>
              );
            })}
          </div>
          {quote.amendedAt && (
            <div
              style={{ fontSize: "11px", color: "#888", marginTop: "8px" }}
            >
              Last amended{" "}
              {new Date(quote.amendedAt).toLocaleDateString()} by{" "}
              {quote.amendedByName || quote.amendedBy || "—"}
            </div>
          )}
        </div>
      )}

      <RevisionSnapshotModal
        snapshot={viewingRevision}
        current={quote}
        onClose={() => setViewingRevision(null)}
      />

      <PdfActionModal
        pending={pdfPending}
        title="Export"
        onPrint={handlePdfPrint}
        onSave={handlePdfSave}
        onCancel={() => setPdfPending(null)}
      />
    </>
  );
}

// ---- Revision snapshot diff + modal ----------------------------------
// num() is already defined at the top of this file; reused here.

const moneyFmt = (n) =>
  Number(n || 0).toLocaleString("en-US", {
    style: "currency",
    currency: "USD",
  });

// Compute customer total for a quote-shaped object (live quote or
// historical snapshot). Mirrors useOrgData.computeQuoteTotal so the
// modal's "old vs new total" line matches what the customer saw.
const snapshotTotal = (q) => {
  const c = q?.costing || {};
  const withMarkup = (l) => num(l.qty) * num(l.cost) * (1 + num(l.markup) / 100);
  const fuelBase = (f) =>
    f.mode === "flat"
      ? num(f.amount)
      : num(f.costPerMile) * num(f.milesOneWay) * 2 * num(f.trips);
  const fuelTotal = (f) => fuelBase(f) * (1 + num(f.markup) / 100);
  const sub =
    (c.labor || []).reduce((s, l) => s + num(l.hours) * num(l.price), 0) +
    (c.materials || []).reduce((s, m) => s + withMarkup(m), 0) +
    (c.charges || []).reduce((s, x) => s + withMarkup(x), 0) +
    (c.fuel || []).reduce((s, f) => s + fuelTotal(f), 0);
  const tax = (c.taxes || []).reduce(
    (s, t) =>
      s + (t.mode === "flat" ? num(t.amount) : sub * (num(t.rate) / 100)),
    0
  );
  return sub + tax;
};

const empty = (v) => v === undefined || v === null || v === "";
const sameText = (a, b) => (a || "") === (b || "");

// Walk two quote-shaped objects and produce a list of human-readable
// changes. Only checks fields that admins actually negotiate over
// (scope, exclusions, terms, validity, line items, total). Skips
// metadata + signatures.
const computeRevisionDiff = (snap, cur) => {
  if (!snap || !cur) return [];
  const out = [];

  if (!sameText(snap.title, cur.title))
    out.push({ kind: "text", label: "Title", from: snap.title, to: cur.title });
  if (!sameText(snap.scope, cur.scope))
    out.push({ kind: "text", label: "Scope", from: snap.scope, to: cur.scope });
  if (!sameText(snap.exclusions, cur.exclusions))
    out.push({
      kind: "text",
      label: "Exclusions",
      from: snap.exclusions,
      to: cur.exclusions,
    });
  if (!sameText(snap.terms, cur.terms))
    out.push({ kind: "text", label: "Terms", from: snap.terms, to: cur.terms });
  if ((snap.validDays ?? 30) !== (cur.validDays ?? 30))
    out.push({
      kind: "scalar",
      label: "Validity (days)",
      from: snap.validDays ?? 30,
      to: cur.validDays ?? 30,
    });

  const sections = [
    { key: "labor", label: "Labor" },
    { key: "materials", label: "Materials" },
    { key: "charges", label: "Other charges" },
    { key: "fuel", label: "Fuel / trip" },
    { key: "taxes", label: "Taxes / fees" },
  ];
  for (const s of sections) {
    const before = snap.costing?.[s.key] || [];
    const after = cur.costing?.[s.key] || [];
    const beforeMap = new Map(before.map((x) => [x.id, x]));
    const afterMap = new Map(after.map((x) => [x.id, x]));
    let added = 0;
    let removed = 0;
    let modified = 0;
    for (const id of afterMap.keys()) {
      if (!beforeMap.has(id)) added += 1;
      else if (JSON.stringify(beforeMap.get(id)) !== JSON.stringify(afterMap.get(id)))
        modified += 1;
    }
    for (const id of beforeMap.keys()) {
      if (!afterMap.has(id)) removed += 1;
    }
    if (added || removed || modified) {
      out.push({
        kind: "section",
        label: s.label,
        added,
        removed,
        modified,
      });
    }
  }

  const oldTotal = snapshotTotal(snap);
  const newTotal = snapshotTotal(cur);
  if (Math.abs(oldTotal - newTotal) > 0.005) {
    out.push({
      kind: "money",
      label: "Customer total",
      from: oldTotal,
      to: newTotal,
    });
  }

  return out;
};

// Compact one-line summary for a text diff (terms / scope / etc.).
// Showing full text twice is overwhelming; admins can scroll the
// snapshot details below to see the original wording if needed.
const summarizeText = (s) => {
  const t = (s || "").trim();
  if (!t) return "(empty)";
  if (t.length <= 60) return `"${t}"`;
  return `"${t.slice(0, 60)}..."`;
};

function RevisionSnapshotModal({ snapshot, current, onClose }) {
  if (!snapshot) return null;
  const diff = computeRevisionDiff(snapshot, current);
  const sentAt = snapshot.sentAt
    ? new Date(snapshot.sentAt).toLocaleDateString()
    : "(never sent)";
  const sigName = snapshot.contractorSignature?.name || "(unsigned)";
  const c = snapshot.costing || {};

  const lineList = (rows, render) =>
    (rows || []).length === 0 ? (
      <div style={{ fontSize: "12px", color: "#888", padding: "4px 0" }}>
        (none)
      </div>
    ) : (
      <table style={{ width: "100%", fontSize: "12px", borderCollapse: "collapse" }}>
        <tbody>
          {rows.map((r, i) => (
            <tr key={r.id || i} style={{ borderBottom: "1px solid #f0f0f0" }}>
              {render(r)}
            </tr>
          ))}
        </tbody>
      </table>
    );

  return (
    <div
      onClick={onClose}
      style={{
        position: "fixed",
        inset: 0,
        background: "rgba(0,0,0,0.5)",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        zIndex: 1500,
        padding: "20px",
      }}
    >
      <div
        onClick={(e) => e.stopPropagation()}
        style={{
          background: "white",
          borderRadius: "8px",
          padding: "20px",
          maxWidth: "780px",
          width: "100%",
          maxHeight: "90vh",
          overflowY: "auto",
          boxShadow: "0 10px 32px rgba(0,0,0,0.3)",
        }}
      >
        <div
          style={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "baseline",
            marginBottom: "8px",
          }}
        >
          <h3 style={{ margin: 0 }}>
            Rev {snapshot.revision || 0}{" "}
            <span
              style={{
                fontSize: "12px",
                color: "#666",
                fontWeight: "normal",
              }}
            >
              · sent {sentAt} · signed by {sigName}
            </span>
          </h3>
          <button
            onClick={onClose}
            style={{
              background: "transparent",
              border: "none",
              cursor: "pointer",
              fontSize: "20px",
              color: "#666",
            }}
            title="Close"
          >
            ×
          </button>
        </div>

        {/* Changes since this revision */}
        <div
          style={{
            padding: "10px 12px",
            background: "#fff8e1",
            border: "1px solid #ffd97a",
            borderRadius: "6px",
            marginBottom: "12px",
          }}
        >
          <strong style={{ fontSize: "13px", color: "#7a5400" }}>
            Changes since this revision
          </strong>
          {diff.length === 0 ? (
            <div style={{ fontSize: "12px", color: "#7a5400", marginTop: "4px" }}>
              No changes detected — current state matches this revision.
            </div>
          ) : (
            <ul
              style={{
                margin: "6px 0 0",
                paddingLeft: "20px",
                fontSize: "12px",
                color: "#222",
              }}
            >
              {diff.map((d, i) => {
                if (d.kind === "section") {
                  const parts = [];
                  if (d.added) parts.push(`${d.added} added`);
                  if (d.removed) parts.push(`${d.removed} removed`);
                  if (d.modified) parts.push(`${d.modified} modified`);
                  return (
                    <li key={i}>
                      <strong>{d.label}:</strong> {parts.join(", ")}
                    </li>
                  );
                }
                if (d.kind === "money") {
                  return (
                    <li key={i}>
                      <strong>{d.label}:</strong> {moneyFmt(d.from)} →{" "}
                      <strong>{moneyFmt(d.to)}</strong>
                    </li>
                  );
                }
                if (d.kind === "scalar") {
                  return (
                    <li key={i}>
                      <strong>{d.label}:</strong> {d.from} →{" "}
                      <strong>{d.to}</strong>
                    </li>
                  );
                }
                // text
                return (
                  <li key={i}>
                    <strong>{d.label}:</strong> changed from{" "}
                    {summarizeText(d.from)} to <strong>{summarizeText(d.to)}</strong>
                  </li>
                );
              })}
            </ul>
          )}
        </div>

        {/* Snapshot details */}
        <h4 style={{ margin: "12px 0 6px", fontSize: "13px" }}>
          What this revision looked like
        </h4>

        <SnapshotField label="Title" value={snapshot.title} />
        <SnapshotField label="Scope" value={snapshot.scope} multiline />
        <SnapshotField label="Exclusions" value={snapshot.exclusions} multiline />
        <SnapshotField label="Terms" value={snapshot.terms} multiline />
        <SnapshotField
          label="Validity"
          value={`${snapshot.validDays ?? 30} days`}
        />

        <div style={{ marginTop: "12px" }}>
          <strong style={{ fontSize: "12px" }}>Labor</strong>
          {lineList(c.labor, (l) => (
            <>
              <td style={{ padding: "4px 6px" }}>{l.description || "—"}</td>
              <td style={{ padding: "4px 6px", textAlign: "right" }}>
                {num(l.hours)} hrs × {moneyFmt(num(l.price))}
              </td>
              <td
                style={{ padding: "4px 6px", textAlign: "right", color: "#1a5fb4" }}
              >
                {moneyFmt(num(l.hours) * num(l.price))}
              </td>
            </>
          ))}
        </div>

        <div style={{ marginTop: "12px" }}>
          <strong style={{ fontSize: "12px" }}>Materials</strong>
          {lineList(c.materials, (m) => (
            <>
              <td style={{ padding: "4px 6px" }}>{m.name || "—"}</td>
              <td style={{ padding: "4px 6px", textAlign: "right" }}>
                {num(m.qty)} × {moneyFmt(num(m.cost))}
                {num(m.markup) ? ` + ${num(m.markup)}%` : ""}
              </td>
              <td
                style={{ padding: "4px 6px", textAlign: "right", color: "#1a5fb4" }}
              >
                {moneyFmt(num(m.qty) * num(m.cost) * (1 + num(m.markup) / 100))}
              </td>
            </>
          ))}
        </div>

        <div style={{ marginTop: "12px" }}>
          <strong style={{ fontSize: "12px" }}>Other charges</strong>
          {lineList(c.charges, (x) => (
            <>
              <td style={{ padding: "4px 6px" }}>{x.description || "—"}</td>
              <td style={{ padding: "4px 6px", textAlign: "right" }}>
                {num(x.qty)} × {moneyFmt(num(x.cost))}
                {num(x.markup) ? ` + ${num(x.markup)}%` : ""}
              </td>
              <td
                style={{ padding: "4px 6px", textAlign: "right", color: "#1a5fb4" }}
              >
                {moneyFmt(num(x.qty) * num(x.cost) * (1 + num(x.markup) / 100))}
              </td>
            </>
          ))}
        </div>

        {(c.fuel || []).length > 0 && (
          <div style={{ marginTop: "12px" }}>
            <strong style={{ fontSize: "12px" }}>Fuel / trip</strong>
            {lineList(c.fuel, (f) => (
              <>
                <td style={{ padding: "4px 6px" }}>
                  {f.mode === "flat"
                    ? `Flat ${moneyFmt(num(f.amount))}`
                    : `${num(f.milesOneWay)} mi × 2 × ${num(f.trips)} trips @ ${moneyFmt(
                        num(f.costPerMile)
                      )}/mi`}
                  {num(f.markup) ? ` + ${num(f.markup)}% markup` : ""}
                </td>
              </>
            ))}
          </div>
        )}

        {(c.taxes || []).length > 0 && (
          <div style={{ marginTop: "12px" }}>
            <strong style={{ fontSize: "12px" }}>Taxes / fees</strong>
            {lineList(c.taxes, (t) => (
              <>
                <td style={{ padding: "4px 6px" }}>{t.name || "Tax"}</td>
                <td style={{ padding: "4px 6px", textAlign: "right" }}>
                  {t.mode === "flat"
                    ? `Flat ${moneyFmt(num(t.amount))}`
                    : `${num(t.rate)}% of taxable`}
                </td>
              </>
            ))}
          </div>
        )}

        <div
          style={{
            marginTop: "16px",
            padding: "10px",
            background: "#f6f8fb",
            borderRadius: "6px",
            display: "flex",
            justifyContent: "space-between",
            alignItems: "baseline",
          }}
        >
          <strong>Customer total at this revision</strong>
          <strong style={{ fontSize: "16px", color: "#1a5fb4" }}>
            {moneyFmt(snapshotTotal(snapshot))}
          </strong>
        </div>
      </div>
    </div>
  );
}

function SnapshotField({ label, value, multiline }) {
  return (
    <div style={{ marginBottom: "6px" }}>
      <div style={{ fontSize: "11px", color: "#666", fontWeight: 600 }}>
        {label}
      </div>
      <div
        style={{
          fontSize: "12px",
          color: "#222",
          whiteSpace: multiline ? "pre-wrap" : "normal",
        }}
      >
        {value || <em style={{ color: "#aaa" }}>(empty)</em>}
      </div>
    </div>
  );
}
